import { z } from "zod";
import { LocalizedTextSchema, QuestionSchema, QuestionRefSchema, VisibilitySchema } from "./common";

// ============================================================================
// RESOLVED TYPES (API responses - expanded objects)
// ============================================================================
// These types represent the format returned by APIs and the "Download Resolved
// Form" feature. All UUID references are resolved to full objects.
//
// Usage:
//   - API responses
//   - "Download Resolved Form" feature
//   - Frontend consumption
//
// Characteristics:
//   - Self-contained (no external references)
//   - Ready for frontend consumption
//   - Generated by resolving all UUID refs recursively
//   - Example: { "_type": "question", "question": { id: "uuid-123", hash: "...", ... } }
// ============================================================================

// Resolved QuestionRef - includes the full Question object
export const ResolvedQuestionRefSchema = QuestionRefSchema.extend({
  question: QuestionSchema,
});

export type ResolvedQuestionRef = z.infer<typeof ResolvedQuestionRefSchema>;

// QuestionGroup with expanded objects (resolved format)
export const QuestionGroupSchema = z.object({
  _type: z.literal("question_group"),
  id: z.number(),
  name: z.string(),
  description: LocalizedTextSchema.optional(),
  questions: z.array(ResolvedQuestionRefSchema),
  updatedAt: z.string(),
});

export type QuestionGroup = z.infer<typeof QuestionGroupSchema>;

// Part with expanded Question/QuestionGroup objects
export const PartSchema = z.discriminatedUnion("_type", [
  z
    .object({
      _type: z.literal("question"),
      question: QuestionSchema,
    })
    .merge(QuestionRefSchema),
  z.object({
    _type: z.literal("question_group"),
    visibility: VisibilitySchema,
    questionGroup: QuestionGroupSchema,
  }),
]);

export type Part = z.infer<typeof PartSchema>;

// Page with expanded objects (resolved format)
export const PageSchema = z.object({
  title: LocalizedTextSchema.optional(),
  parts: z.array(PartSchema),
});

export type Page = z.infer<typeof PageSchema>;

// Book with expanded objects (resolved format)
export const BookSchema = z.object({
  _type: z.literal("book"),
  id: z.number(),
  pages: z.array(PageSchema),
  name: z.string(),
  description: LocalizedTextSchema.optional(),
  footer: LocalizedTextSchema.optional(),
  updatedAt: z.string(),
});

export type Book = z.infer<typeof BookSchema>;

// Validation functions
export const validateQuestionGroup = (data: unknown) => {
  return QuestionGroupSchema.safeParse(data);
};

export const validateBook = (data: unknown) => {
  return BookSchema.safeParse(data);
};
